import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";
import { Loader2, Smartphone, Upload } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter
} from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import apiClient from "../lib/api";
import { toastApiError } from "../lib/toastApiError";

const PurchasePaymentModal = ({ open, onOpenChange, purchase, onSuccess }) => {
  const { t } = useTranslation("pharmacy");
  const [accounts, setAccounts] = useState({});
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [provider, setProvider] = useState("");
  const [senderPhone, setSenderPhone] = useState("");
  const [senderName, setSenderName] = useState("");
  const [transactionReference, setTransactionReference] = useState("");
  const [comment, setComment] = useState("");
  const [proofFile, setProofFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    setLoadingAccounts(true);
    apiClient.get("/purchases/mobilemoney-accounts")
      .then((res) => setAccounts(res.data.accounts || {}))
      .catch(() => toast.error(t("purchasePayment.loadMethodsError")))
      .finally(() => setLoadingAccounts(false));
  }, [open, t]);

  const resetForm = () => {
    setProvider("");
    setSenderPhone("");
    setSenderName("");
    setTransactionReference("");
    setComment("");
    setProofFile(null);
  };

  const handleSubmit = async () => {
    if (!purchase?.purchase_id && !purchase?.id) {
      toast.error(t("purchasePayment.purchaseNotFound"));
      return;
    }
    if (!provider || !senderPhone.trim() || !senderName.trim() || !transactionReference.trim()) {
      toast.error(t("purchasePayment.fillRequired"));
      return;
    }

    const purchaseId = purchase.purchase_id || purchase.id;
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append("provider", provider);
      formData.append("sender_phone", senderPhone.trim());
      formData.append("sender_name", senderName.trim());
      formData.append("transaction_reference", transactionReference.trim());
      if (comment.trim()) formData.append("comment", comment.trim());
      if (proofFile) formData.append("proof_file", proofFile);

      const res = await apiClient.post(`/purchases/${purchaseId}/mobilemoney/request`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      toast.success(res.data?.message || t("purchasePayment.submitted"));
      resetForm();
      onOpenChange(false);
      onSuccess?.();
    } catch (error) {
      toastApiError(error);
    } finally {
      setSubmitting(false);
    }
  };

  const amount = purchase?.grand_total ?? purchase?.total_price ?? 0;
  const accountList = Object.entries(accounts);
  const formattedAmount = Number(amount).toLocaleString();

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) resetForm(); onOpenChange(v); }}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="font-heading flex items-center gap-2">
            <Smartphone className="h-5 w-5 text-omu-blue-500" />
            {t("purchasePayment.title")}
          </DialogTitle>
          <DialogDescription>
            {purchase?.medicine_name
              ? t("purchasePayment.description", { medicine: `${purchase.medicine_name} — `, amount: formattedAmount })
              : t("purchasePayment.descriptionNoMed", { amount: formattedAmount })}
          </DialogDescription>
        </DialogHeader>

        {loadingAccounts ? (
          <div className="flex justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-omu-blue-500" />
          </div>
        ) : accountList.length === 0 ? (
          <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded-lg p-3">
            {t("purchasePayment.noMethods")}
          </p>
        ) : (
          <div className="space-y-4 py-2">
            <div className="rounded-xl border bg-slate-50 p-3 space-y-2">
              <p className="text-xs font-medium text-slate-600 uppercase tracking-wide">{t("purchasePayment.omuAccounts")}</p>
              {accountList.map(([key, acc]) => (
                <div key={key} className="text-sm flex justify-between gap-2">
                  <span className="font-medium">{acc.name || key}</span>
                  <span className="text-slate-700">{acc.id} — {acc.holder}</span>
                </div>
              ))}
            </div>

            <div className="space-y-2">
              <Label>{t("purchasePayment.paymentMethod")}</Label>
              <Select value={provider || undefined} onValueChange={setProvider}>
                <SelectTrigger className="h-11 rounded-xl">
                  <SelectValue placeholder={t("purchasePayment.paymentMethodPlaceholder")} />
                </SelectTrigger>
                <SelectContent>
                  {accountList.map(([key, acc]) => (
                    <SelectItem key={key} value={key}>{acc.name || key}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid sm:grid-cols-2 gap-3">
              <div className="space-y-2">
                <Label>{t("purchasePayment.yourName")}</Label>
                <Input value={senderName} onChange={(e) => setSenderName(e.target.value)} className="rounded-xl" />
              </div>
              <div className="space-y-2">
                <Label>{t("purchasePayment.yourPhone")}</Label>
                <Input value={senderPhone} onChange={(e) => setSenderPhone(e.target.value)} className="rounded-xl" />
              </div>
            </div>

            <div className="space-y-2">
              <Label>{t("purchasePayment.transactionRef")}</Label>
              <Input
                value={transactionReference}
                onChange={(e) => setTransactionReference(e.target.value)}
                placeholder={t("purchasePayment.transactionRefPlaceholder")}
                className="rounded-xl"
              />
            </div>

            <div className="space-y-2">
              <Label>{t("purchasePayment.commentOptional")}</Label>
              <Textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={2} />
            </div>

            <div className="space-y-2">
              <Label>{t("purchasePayment.proof")}</Label>
              <label className="flex items-center gap-2 cursor-pointer text-sm text-omu-blue-600">
                <Upload className="h-4 w-4" />
                <input type="file" accept="image/*" className="hidden" onChange={(e) => setProofFile(e.target.files?.[0] || null)} />
                {proofFile ? proofFile.name : t("purchasePayment.attachProof")}
              </label>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>{t("actions.later")}</Button>
          <Button
            className="bg-omu-blue-500 hover:bg-omu-blue-600"
            onClick={handleSubmit}
            disabled={submitting || loadingAccounts || accountList.length === 0}
          >
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : t("purchasePayment.submitPayment")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PurchasePaymentModal;
